import React, { useState, useEffect } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Settings,
  LogOut,
  Folder,
  FileText,
  Container,
  Menu,
  X,
  MessageCircle,
  CreditCard
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const SidebarLink = ({ to, icon: Icon, label, active, onClick }) => (
  <Link to={to} onClick={onClick} className="relative group block">
    <div
      className={`
        relative z-10
        flex items-center 
        gap-3 
        px-4 
        py-3 
        rounded-xl transition-all duration-300
        ${active ? "text-orange-500" : "text-gray-500 hover:text-gray-200"}
      `}
    >
      <Icon size={20} className={`shrink-0 transition-transform duration-300 ${active ? "scale-110" : "group-hover:scale-110"}`} />
      <span className="font-bold text-sm tracking-tight truncate">{label}</span>

      {active && (
        <motion.div
          layoutId="sidebar-active"
          className="absolute inset-0 bg-orange-500/10 border border-orange-500/20 rounded-xl z-[-1]"
          transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
        />
      )}
    </div>
  </Link>
);

export default function DashboardLayout() {
  const location = useLocation();
  const { logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Prevent scrolling when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isSidebarOpen]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex font-sans overflow-hidden">

      {/* MOBILE TOP BAR (Hidden on MD+) */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-16 bg-[#080808]/80 backdrop-blur-xl border-b border-white/5 z-[60] flex items-center justify-between px-5">
        <Link to="/dashboard/home" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/20">
            <Container size={16} className="text-white" strokeWidth={2.5} />
          </div>
          <span className="font-black text-lg tracking-tighter">EnvDock</span>
        </Link>
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 text-gray-400 hover:text-white bg-white/5 rounded-lg"
          >
            <Menu size={24} />
          </button>
          <button
            onClick={() => logout()}
            className="p-2.5 text-gray-400 hover:text-white bg-white/5 rounded-lg"
          >
            <LogOut size={20}  />
          </button>
        </div>

      </header>

      {/* MOBILE OVERLAY */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsSidebarOpen(false)}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] md:hidden"
          />
        )}
      </AnimatePresence>

      {/* SIDEBAR (Drawer on Mobile, Persistent on MD+) */}

      {/* z-[80] md:z-50  */}
      <aside
        className={`
          fixed md:sticky top-0 h-screen 
          xl:w-72 w-52
          z-[80] md:z-0
          bg-[#080808] border-r border-white/5 
          flex flex-col 
          shrink-0 
          transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
      >
        {/* Background Noise/Glow */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />

        {/* Sidebar Header */}
        <div className="h-20 flex items-center justify-between px-8 border-b border-white/5 relative z-10">
          <Link className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20 shrink-0 transition-transform group-hover:scale-105">
              <Container size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-xl tracking-tighter">EnvDock</span>
          </Link>

          {/* <button onClick={() => setIsSidebarOpen(false)} className="md:hidden text-gray-500 hover:text-white">
            <X size={20} />
          </button> */}
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 py-8 space-y-1 px-4 overflow-y-auto custom-scrollbar relative z-10">
          <SidebarLink
            to="/dashboard/home"
            icon={LayoutDashboard}
            label="Dashboard"
            active={location.pathname === "/dashboard/home"}
          />
          <SidebarLink
            to="/dashboard/projects"
            icon={Folder}
            label="Project Vaults"
            active={location.pathname.includes("projects")}
          />
          <SidebarLink
            to="/dashboard/docs"
            icon={FileText}
            label="Documentation"
            active={location.pathname === "/dashboard/docs"}
          />
          <SidebarLink
            to="/dashboard/settings"
            icon={Settings}
            label="Settings"
            active={location.pathname === "/dashboard/settings"}
          />
          <SidebarLink
            to="/dashboard/subscription"
            icon={CreditCard}
            label="Subscription"
            active={location.pathname === "/dashboard/subscription"}
          />
          <SidebarLink
            to="/dashboard/support"
            icon={MessageCircle}
            label="Support"
            active={location.pathname === "/dashboard/support"}
          />
        </nav>

        {/* Footer / User Actions */}
        <div className="md:block hidden p-4 border-t border-white/5 relative z-10 bg-[#080808]">
          <button
            onClick={logout}
            className="
              w-full flex items-center 
              gap-3 
              px-4 
              py-3 
              text-gray-500 hover:text-red-500 
              hover:bg-red-500/5 
              rounded-xl
              transition-all duration-200
              group
            "
          >
            <LogOut size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span className="font-bold text-sm tracking-tight">Sign-out</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 min-w-0 relative h-screen overflow-y-auto md:pt-0 pt-16">
        {/* Background glow remains the same */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-600/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-6xl mx-auto p-6 md:p-12 relative z-10">
          {/* We can add a simple Breadcrumb or Welcome message here for mobile */}
          <Outlet />
        </div>
      </main>
    </div>
  );
}